import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Turnstile } from './Turnstile';
import { useAuth } from '../context';
import type { LoginRequest } from '../types';
import logo from '../../logo.png';

// Схема валидации для формы логина
const loginSchema = z.object({
  username: z
    .string()
    .min(4, 'Имя пользователя должно содержать минимум 4 символа')
    .regex(/^[a-zA-Z]+$/, 'Имя пользователя должно содержать только английские буквы'),
  password: z
    .string()
    .min(10, 'Пароль должен содержать минимум 10 символов')
    .regex(/^(?=.*[a-zA-Z])(?=.*\d)/, 'Пароль должен содержать буквы и цифры'),
});

type LoginFormData = z.infer<typeof loginSchema>;

interface LoginFormProps {
  onSuccess?: () => void;
  onSwitchToRegister?: () => void;
}

export function LoginForm({ onSuccess, onSwitchToRegister }: LoginFormProps) {
  const { login, isLoading } = useAuth();
  const [turnstileToken, setTurnstileToken] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      username: '',
      password: '',
    },
  });

  const onSubmit = async (data: LoginFormData) => {
    if (!turnstileToken) {
      setError('Пожалуйста, пройдите проверку безопасности');
      return;
    }

    try {
      setIsSubmitting(true);
      setError('');

      const loginData: LoginRequest = {
        ...data,
        turnstile_token: turnstileToken,
      };

      await login(loginData);
      onSuccess?.();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Ошибка авторизации';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTurnstileVerify = (token: string) => {
    setTurnstileToken(token);
    setError(''); // Очищаем ошибку при успешной верификации
  };

  const handleTurnstileError = (error: string) => {
    setTurnstileToken('');
    setError('Ошибка проверки безопасности. Попробуйте еще раз.');
  };

  const isFormDisabled = isLoading || isSubmitting;

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="space-y-1">
        <div className="flex justify-center mb-4">
          <img src={logo} alt="RUTON" className="w-24 h-24 invert" />
        </div>
        <CardTitle className="text-2xl font-bold text-center">Вход в систему</CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Имя пользователя</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Введите имя пользователя"
                      disabled={isFormDisabled}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Пароль</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      placeholder="Введите пароль"
                      disabled={isFormDisabled}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-2">
              <Label className="text-sm font-medium">Проверка безопасности</Label>
              <Turnstile
                onVerify={handleTurnstileVerify}
                onError={handleTurnstileError}
                className="flex justify-center"
              />
            </div>

            {error && (
              <div className="text-sm text-destructive font-medium text-center">
                {error}
              </div>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={isFormDisabled || !turnstileToken}
            >
              {isSubmitting ? 'Вход...' : 'Войти'}
            </Button>

            {onSwitchToRegister && (
              <div className="text-center text-sm">
                <span className="text-muted-foreground">Нет аккаунта? </span>
                <Button
                  type="button"
                  variant="link"
                  className="p-0 h-auto font-normal"
                  onClick={onSwitchToRegister}
                  disabled={isFormDisabled}
                >
                  Зарегистрироваться
                </Button>
              </div>
            )}
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
